import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Wrench, 
  RefreshCw, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  Bell,
  Settings,
  Calendar,
  Gauge,
  Loader2,
  Search,
  Plus,
  X,
  RotateCcw,
  Truck,
  HardHat
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
}

export default function ServiceTrackingPage() {
  const [activeTab, setActiveTab] = useState('trucks');
  
  // Trucks state
  const [trucksLoading, setTrucksLoading] = useState(true);
  const [trucksRefreshing, setTrucksRefreshing] = useState(false);
  const [trucksError, setTrucksError] = useState(null);
  const [trucksData, setTrucksData] = useState({ vehicles: [], summary: {} });
  const [trucksSearch, setTrucksSearch] = useState('');
  const [trucksFilter, setTrucksFilter] = useState('all');
  
  // Equipment state
  const [equipmentLoading, setEquipmentLoading] = useState(true);
  const [equipmentRefreshing, setEquipmentRefreshing] = useState(false);
  const [equipmentError, setEquipmentError] = useState(null);
  const [equipmentData, setEquipmentData] = useState({ equipment: [], summary: {} });
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  
  // Dialog state
  const [showSetDeadline, setShowSetDeadline] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState('truck');
  const [deadlineHours, setDeadlineHours] = useState('');
  const [serviceInterval, setServiceInterval] = useState('400');
  const [saving, setSaving] = useState(false);
  
  // Reset confirmation state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showResetFinalConfirm, setShowResetFinalConfirm] = useState(false);
  const [resetItem, setResetItem] = useState(null);
  const [resetType, setResetType] = useState('truck');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchTrucksData();
    fetchEquipmentData();
  }, []);

  const fetchTrucksData = async () => {
    setTrucksRefreshing(true);
    const token = localStorage.getItem('vaultai_token');
    if (!token) {
      setTrucksError('Not authenticated');
      setTrucksLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/titan-gps/service-tracking`, { headers });
      
      if (response.data.success) {
        setTrucksData(response.data);
        setTrucksError(null);
      } else {
        setTrucksError(response.data.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Trucks fetch error:', err);
      setTrucksError(err.message);
    } finally {
      setTrucksLoading(false);
      setTrucksRefreshing(false);
    }
  };

  const fetchEquipmentData = async () => {
    setEquipmentRefreshing(true);
    const token = localStorage.getItem('vaultai_token');
    if (!token) {
      setEquipmentError('Not authenticated');
      setEquipmentLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/fleetio/equipment-service-tracking`, { headers });
      
      if (response.data.success) {
        setEquipmentData(response.data);
        setEquipmentError(null);
      } else {
        setEquipmentError(response.data.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Equipment fetch error:', err);
      setEquipmentError(err.message);
    } finally {
      setEquipmentLoading(false);
      setEquipmentRefreshing(false);
    }
  };

  const handleSetDeadline = async () => {
    if (!selectedItem || !deadlineHours) return;
    
    setSaving(true);
    const token = localStorage.getItem('vaultai_token');
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (selectedType === 'truck') {
        const params = new URLSearchParams({
          device_id: selectedItem.device_id,
          service_deadline_hours: deadlineHours,
          service_interval: serviceInterval
        });
        await axios.post(`${API}/titan-gps/service-deadline?${params}`, null, { headers });
        fetchTrucksData();
      } else {
        const params = new URLSearchParams({
          equipment_id: selectedItem.equipment_id,
          service_deadline_hours: deadlineHours,
          service_interval: serviceInterval
        });
        await axios.post(`${API}/fleetio/equipment-service-deadline?${params}`, null, { headers });
        fetchEquipmentData();
      }
      
      setShowSetDeadline(false);
      setSelectedItem(null);
      setDeadlineHours('');
    } catch (err) {
      console.error('Set deadline error:', err);
      alert('Failed to set deadline');
    } finally {
      setSaving(false);
    }
  };

  const handleResetClick = (item, type) => {
    setResetItem(item);
    setResetType(type);
    setShowResetConfirm(true);
  };

  const handleFirstConfirm = () => {
    setShowResetConfirm(false);
    setShowResetFinalConfirm(true);
  };

  const handleResetService = async () => {
    if (!resetItem) return;
    
    setResetting(true);
    const token = localStorage.getItem('vaultai_token');
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (resetType === 'truck') {
        // For trucks - keep the existing behavior (set new deadline)
        const currentHours = resetItem.current_hours || 0;
        const interval = resetItem.service_interval || 400;
        const newDeadline = Math.ceil(currentHours + interval);
        const params = new URLSearchParams({
          device_id: resetItem.device_id,
          service_deadline_hours: newDeadline.toString(),
          service_interval: interval.toString()
        });
        await axios.post(`${API}/titan-gps/service-deadline?${params}`, null, { headers });
        fetchTrucksData();
      } else {
        // For equipment - clear the deadline entirely
        const params = new URLSearchParams({
          equipment_id: resetItem.equipment_id,
          equipment_name: resetItem.equipment_name
        });
        await axios.post(`${API}/fleetio/equipment-service-reset?${params}`, null, { headers });
        fetchEquipmentData();
      }
      
      setShowResetFinalConfirm(false);
      setResetItem(null);
    } catch (err) {
      console.error('Reset error:', err);
      alert('Failed to reset service deadline');
    } finally {
      setResetting(false);
    }
  };

  const openSetDeadline = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
    const currentHours = type === 'truck' ? item.current_hours : item.current_hours;
    const suggested = item.service_deadline_hours || Math.ceil((currentHours + 400) / 100) * 100;
    setDeadlineHours(suggested.toString());
    setServiceInterval(item.service_interval?.toString() || '400');
    setShowSetDeadline(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'overdue':
        return <Badge className="bg-red-500 text-white">OVERDUE</Badge>;
      case 'critical':
        return <Badge className="bg-orange-500 text-white">CRITICAL</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-500 text-black">WARNING</Badge>;
      case 'ok':
        return <Badge className="bg-green-500 text-white">OK</Badge>;
      default:
        return <Badge variant="outline">No Deadline</Badge>;
    }
  };

  const filterItems = (items, search, filter, nameKey) => {
    return items.filter(item => {
      if (filter === 'with_deadline' && !item.service_deadline_hours) return false;
      if (filter === 'without_deadline' && item.service_deadline_hours) return false;
      if (filter === 'urgent' && !['overdue', 'critical', 'warning'].includes(item.status)) return false;
      
      if (search) {
        const name = item[nameKey]?.toLowerCase() || '';
        return name.includes(search.toLowerCase());
      }
      return true;
    });
  };

  const renderVehicleCard = (vehicle, idx, type) => {
    const isTruck = type === 'truck';
    const name = isTruck ? vehicle.device_name : vehicle.equipment_name;
    const id = isTruck ? vehicle.device_id : vehicle.equipment_id;
    const hasDeadline = vehicle.service_deadline_hours !== null && vehicle.service_deadline_hours !== undefined;
    
    return (
      <Card 
        key={id || idx} 
        className={`hover:shadow-md transition-shadow ${
          vehicle.status === 'overdue' ? 'border-red-300 bg-red-50/30' :
          vehicle.status === 'critical' ? 'border-orange-300 bg-orange-50/30' :
          vehicle.status === 'warning' ? 'border-yellow-300 bg-yellow-50/30' :
          ''
        }`}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${
                vehicle.status === 'overdue' ? 'bg-red-100' :
                vehicle.status === 'critical' ? 'bg-orange-100' :
                vehicle.status === 'warning' ? 'bg-yellow-100' :
                'bg-slate-100'
              }`}>
                <Gauge className={`h-6 w-6 ${
                  vehicle.status === 'overdue' ? 'text-red-600' :
                  vehicle.status === 'critical' ? 'text-orange-600' :
                  vehicle.status === 'warning' ? 'text-yellow-600' :
                  'text-slate-600'
                }`} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-foreground">{name}</h3>
                {isTruck && vehicle.unit_number && vehicle.unit_number !== vehicle.device_name && (
                  <p className="text-sm text-foreground/70">Unit: {vehicle.unit_number}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Current Hours */}
              <div className="text-center min-w-[80px]">
                <p className="text-2xl font-bold text-foreground">{vehicle.current_hours?.toLocaleString() || 0}</p>
                <p className="text-xs text-foreground/70">Current Hours</p>
              </div>
              
              {/* Service At */}
              <div className="text-center min-w-[80px]">
                <p className="text-2xl font-bold text-blue-400">
                  {hasDeadline ? vehicle.service_deadline_hours?.toLocaleString() : '—'}
                </p>
                <p className="text-xs text-foreground/70">Service At</p>
              </div>
              
              {/* Hours Remaining / Overdue */}
              <div className="text-center min-w-[100px]">
                {hasDeadline && vehicle.hours_remaining !== null && vehicle.hours_remaining !== undefined ? (
                  <>
                    <p className={`text-2xl font-bold ${
                      vehicle.hours_remaining < 0 ? 'text-red-400' :
                      vehicle.hours_remaining < 50 ? 'text-orange-400' :
                      vehicle.hours_remaining < 100 ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {vehicle.hours_remaining < 0 ? 
                        `+${Math.abs(Math.round(vehicle.hours_remaining)).toLocaleString()}` : 
                        Math.round(vehicle.hours_remaining).toLocaleString()}
                    </p>
                    <p className="text-xs text-foreground/70">
                      {vehicle.hours_remaining < 0 ? 'Overdue' : 'Remaining'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-slate-500">—</p>
                    <p className="text-xs text-foreground/70">Remaining</p>
                  </>
                )}
              </div>
              
              {/* Status Badge */}
              <div className="min-w-[90px] text-right">
                {getStatusBadge(vehicle.status)}
              </div>
              
              {/* RESET Button */}
              {hasDeadline ? (
                <Button 
                  variant="destructive"
                  size="sm"
                  onClick={() => handleResetClick(vehicle, type)}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  RESET
                </Button>
              ) : (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => openSetDeadline(vehicle, type)}
                  className="text-blue-600 border-blue-300"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  SET
                </Button>
              )}
              
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => openSetDeadline(vehicle, type)}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Additional Info Row - only for trucks with deadlines */}
          {isTruck && hasDeadline && (
            <div className="mt-3 pt-3 border-t flex gap-6 text-sm text-foreground/70">
              {vehicle.avg_hours_per_day ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Avg: {vehicle.avg_hours_per_day} hrs/day
                </span>
              ) : (
                <span className="flex items-center gap-1 text-foreground/50">
                  <Clock className="h-3 w-3" />
                  Avg: Recording data...
                </span>
              )}
              {vehicle.estimated_service_date ? (
                <span className="flex items-center gap-1 text-blue-400">
                  <Calendar className="h-3 w-3" />
                  Est. Service: {formatDate(vehicle.estimated_service_date)}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-foreground/50">
                  <Calendar className="h-3 w-3" />
                  Est. Service: Need more data
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const filteredTrucks = filterItems(trucksData.vehicles || [], trucksSearch, trucksFilter, 'device_name');
  const filteredEquipment = filterItems(equipmentData.equipment || [], equipmentSearch, equipmentFilter, 'equipment_name');

  const itemName = selectedType === 'truck' ? selectedItem?.device_name : selectedItem?.equipment_name;
  const resetName = resetType === 'truck' ? resetItem?.device_name : resetItem?.equipment_name;

  return (
    <div className="space-y-6" data-testid="service-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <Wrench className="h-6 w-6 text-orange-500" />
            400 Hour Service Tracking
          </h1>
          <p className="text-muted-foreground text-sm">Track vehicle and equipment service deadlines</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="trucks" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            GPS Trucks
          </TabsTrigger>
          <TabsTrigger value="equipment" className="flex items-center gap-2">
            <HardHat className="w-4 h-4" />
            Equipment (900s)
          </TabsTrigger>
        </TabsList>

        {/* TRUCKS TAB */}
        <TabsContent value="trucks" className="mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="cursor-pointer hover:border-blue-300" onClick={() => setTrucksFilter('all')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{trucksData.summary?.total_vehicles || 0}</p>
                <p className="text-sm text-foreground/70">Total Vehicles</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-red-300" onClick={() => setTrucksFilter('urgent')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{trucksData.summary?.overdue || 0}</p>
                <p className="text-sm text-foreground/70">Overdue</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-orange-300" onClick={() => setTrucksFilter('urgent')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">{trucksData.summary?.approaching_service || 0}</p>
                <p className="text-sm text-foreground/70">Approaching</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-green-300" onClick={() => setTrucksFilter('with_deadline')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{trucksData.summary?.with_deadlines || 0}</p>
                <p className="text-sm text-foreground/70">With Deadlines</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-slate-300" onClick={() => setTrucksFilter('without_deadline')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-slate-400">{trucksData.summary?.without_deadlines || 0}</p>
                <p className="text-sm text-foreground/70">No Deadline</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Refresh */}
          <div className="flex gap-4 items-center mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search trucks..."
                value={trucksSearch}
                onChange={(e) => setTrucksSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {trucksFilter !== 'all' && (
              <Badge 
                variant="secondary" 
                className="cursor-pointer py-1.5" 
                onClick={() => setTrucksFilter('all')}
              >
                {trucksFilter.replace('_', ' ')} <X className="h-3 w-3 ml-1" />
              </Badge>
            )}
            <Button onClick={fetchTrucksData} disabled={trucksRefreshing} variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${trucksRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {trucksError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-4">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              {trucksError}
            </div>
          )}

          {trucksLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-lg">Loading trucks...</span>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredTrucks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No trucks found matching your criteria
                </div>
              ) : (
                filteredTrucks.map((truck, idx) => renderVehicleCard(truck, idx, 'truck'))
              )}
            </div>
          )}
        </TabsContent>

        {/* EQUIPMENT TAB */}
        <TabsContent value="equipment" className="mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="cursor-pointer hover:border-blue-300" onClick={() => setEquipmentFilter('all')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{equipmentData.summary?.total || 0}</p>
                <p className="text-sm text-foreground/70">Total Equipment</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-red-300" onClick={() => setEquipmentFilter('urgent')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{equipmentData.summary?.overdue || 0}</p>
                <p className="text-sm text-foreground/70">Overdue</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-orange-300" onClick={() => setEquipmentFilter('urgent')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">{equipmentData.summary?.approaching_service || 0}</p>
                <p className="text-sm text-foreground/70">Approaching</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-green-300" onClick={() => setEquipmentFilter('with_deadline')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{equipmentData.summary?.with_deadlines || 0}</p>
                <p className="text-sm text-foreground/70">With Deadlines</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:border-slate-300" onClick={() => setEquipmentFilter('without_deadline')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-slate-400">{(equipmentData.summary?.total || 0) - (equipmentData.summary?.with_deadlines || 0)}</p>
                <p className="text-sm text-foreground/70">No Deadline</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Refresh */}
          <div className="flex gap-4 items-center mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search equipment..."
                value={equipmentSearch}
                onChange={(e) => setEquipmentSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {equipmentFilter !== 'all' && (
              <Badge 
                variant="secondary" 
                className="cursor-pointer py-1.5" 
                onClick={() => setEquipmentFilter('all')}
              >
                {equipmentFilter.replace('_', ' ')} <X className="h-3 w-3 ml-1" />
              </Badge>
            )}
            <Button onClick={fetchEquipmentData} disabled={equipmentRefreshing} variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${equipmentRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {equipmentError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-4">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              {equipmentError}
            </div>
          )}

          {equipmentLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-lg">Loading equipment...</span>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredEquipment.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No equipment found matching your criteria
                </div>
              ) : (
                filteredEquipment.map((equip, idx) => renderVehicleCard(equip, idx, 'equipment'))
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Set Deadline Dialog */}
      <Dialog open={showSetDeadline} onOpenChange={setShowSetDeadline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Service Deadline</DialogTitle>
            <DialogDescription>
              Set the next service deadline for {itemName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground">Current Hours</label>
              <p className="text-xl font-bold">
                {(selectedItem?.current_hours || 0).toLocaleString()} hours
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground">Service Deadline (hours)</label>
              <Input
                type="number"
                value={deadlineHours}
                onChange={(e) => setDeadlineHours(e.target.value)}
                placeholder="e.g., 12400"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground">Service Interval (hours)</label>
              <Input
                type="number"
                value={serviceInterval}
                onChange={(e) => setServiceInterval(e.target.value)}
                placeholder="400"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetDeadline(false)}>
              Cancel
            </Button>
            <Button onClick={handleSetDeadline} disabled={saving || !deadlineHours}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Deadline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset First Confirmation */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resetType === 'truck' ? 'Reset Service for' : 'Clear Deadline for'} {resetName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {resetType === 'truck' ? (
                <>
                  This will set a new service deadline at {((resetItem?.current_hours || 0) + (resetItem?.service_interval || 400)).toLocaleString()} hours.
                  <br /><br />
                  Current hours: <span className="font-bold">{(resetItem?.current_hours || 0).toLocaleString()}</span>
                  <br />
                  Service interval: <span className="font-bold">{resetItem?.service_interval || 400}</span> hours
                </>
              ) : (
                <>
                  This will <span className="font-bold text-red-500">clear the service deadline</span> and return it to "No Deadline" state.
                  <br /><br />
                  Current deadline: <span className="font-bold">{resetItem?.service_deadline_hours?.toLocaleString() || 'N/A'}</span> hours
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleFirstConfirm} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Final Confirmation */}
      <AlertDialog open={showResetFinalConfirm} onOpenChange={setShowResetFinalConfirm}>
        <AlertDialogContent className="border-red-500/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500">⚠️ Final Confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              {resetType === 'truck' ? (
                <>
                  Are you absolutely sure you want to reset the service deadline for <span className="font-bold">{resetName}</span>?
                </>
              ) : (
                <>
                  Are you absolutely sure you want to <span className="font-bold">clear the deadline</span> for <span className="font-bold">{resetName}</span>?
                  <br /><br />
                  This will remove the service tracking for this equipment.
                </>
              )}
              <br /><br />
              This action will be logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetService} 
              disabled={resetting}
              className="bg-red-500 hover:bg-red-600"
            >
              {resetting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <RotateCcw className="w-4 h-4 mr-2" />
              {resetType === 'truck' ? 'Yes, Reset Service' : 'Yes, Clear Deadline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
